import React, { useMemo } from 'react';
import { IconButton, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FolderIcon from '@material-ui/icons/Folder';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import { TreeItem, TreeView } from '@material-ui/lab';
import { FOLDER_ICON_COLOR } from '../../scripts/constants';
import { buildUploadPreviewTree, UploadTreeNode } from './upload-plan-utils';

const collectNodeIds = (node: UploadTreeNode, prefix = ''): string[] =>
  [...node.children.entries()].flatMap(([name, child]) => {
    const nodeId = `${prefix}${name}/`;
    return [nodeId, ...collectNodeIds(child, nodeId)];
  });

const useStyles = makeStyles(() =>
  createStyles({
    treeItemLabel: {
      display: 'flex',
      alignItems: 'center',
      padding: '2px 0px',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    folderIcon: {
      color: FOLDER_ICON_COLOR,
      marginRight: 8,
    },
    fileIcon: {
      color: '#7A797A',
      marginRight: 8,
    },
    name: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    removeButton: {
      padding: 2,
      marginLeft: 4,
    },
    container: {
      maxHeight: 260,
      overflowY: 'auto',
      marginTop: 8,
      width: '100%',
    },
  }),
);

interface UploadPreviewTreeProps {
  files: File[];
  /**
   * The folders from the project root down to the folder being uploaded into, outermost
   * first, so the preview always shows where in the project the upload lands.
   */
  rootPath: string[];
  removeFile: (file: File) => void;
}

/** Node id for the nth folder of the target path; the deepest one holds the upload. */
const rootPathNodeId = (index: number) => `root-${index}`;

/**
 * What an upload is about to create, laid out the way CENTERLINE Drive and the default folder
 * structure editor lay folders out: the path from the project root down to the target folder,
 * the folders the upload brings with it nested inside, and each file removable before anything
 * is sent.
 */
export default function UploadPreviewTree(props: UploadPreviewTreeProps) {
  const { files, rootPath, removeFile } = props;
  const classes = useStyles();

  const root = useMemo(() => buildUploadPreviewTree(files), [files]);
  // The same '/' prefix renderFolders uses, or the ids would not match and nothing expands.
  const expandedNodeIds = useMemo(
    () => [...rootPath.map((_, index) => rootPathNodeId(index)), ...collectNodeIds(root, '/')],
    [root, rootPath],
  );

  const renderFiles = (node: UploadTreeNode, prefix: string) =>
    [...node.files]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((file, index) => (
        <TreeItem
          // Two files can share a name only across folders, and the prefix separates those.
          key={`${prefix}${file.name}-${index}`}
          nodeId={`${prefix}${file.name}-${index}`}
          classes={{ label: classes.treeItemLabel }}
          label={
            <>
              <InsertDriveFileOutlinedIcon fontSize="small" className={classes.fileIcon} />
              <Typography variant="body2" className={classes.name}>
                {file.name}
              </Typography>
              <Tooltip title="Remove">
                <IconButton
                  className={classes.removeButton}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeFile(file);
                  }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          }
        />
      ));

  const renderFolders = (node: UploadTreeNode, prefix: string) =>
    [...node.children.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, child]) => {
        const nodeId = `${prefix}${name}/`;
        return (
          <TreeItem
            key={nodeId}
            nodeId={nodeId}
            classes={{ label: classes.treeItemLabel }}
            label={folderLabel(name)}
          >
            {renderFolders(child, nodeId)}
            {renderFiles(child, nodeId)}
          </TreeItem>
        );
      });

  const folderLabel = (name: string) => (
    <>
      <FolderIcon className={classes.folderIcon} />
      <Typography variant="body2" className={classes.name}>
        {name}
      </Typography>
    </>
  );

  // The target path, one folder inside the next, with the upload's own content at the bottom.
  const renderRootPath = (index: number): React.ReactNode => (
    <TreeItem
      nodeId={rootPathNodeId(index)}
      classes={{ label: classes.treeItemLabel }}
      label={folderLabel(rootPath[index])}
    >
      {index < rootPath.length - 1 ? (
        renderRootPath(index + 1)
      ) : (
        <>
          {renderFolders(root, '/')}
          {renderFiles(root, '/')}
        </>
      )}
    </TreeItem>
  );

  if (!files.length || !rootPath.length) return null;

  return (
    <TreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
      expanded={expandedNodeIds}
      selected={[]}
      className={classes.container}
    >
      {renderRootPath(0)}
    </TreeView>
  );
}
